import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
} from "@mui/material";
import { Button } from "../../theme/components/index.js";
import {
  acceptConsent,
  getCurrentVersion,
  downloadConsentDocument,
} from "../../services/consentService.js";
import { useSnackbar } from "notistack";

function ConsentDocumentModal({
  open,
  consentStatus,
  documentType,
  onAccept,
  onDecline,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);

  useEffect(() => {
    if (open && consentStatus) {
      const currentVersion = getCurrentVersion(consentStatus, documentType);
      setVersionInfo(currentVersion);
      setIsChecked(false);
    }
  }, [open, consentStatus]);

  const handleAccept = async () => {
    if (!isChecked || !versionInfo) {
      return;
    }

    setIsLoading(true);
    try {
      await acceptConsent(versionInfo.id);
      enqueueSnackbar("Datenschutzeinwilligung erfolgreich bestätigt", {
        variant: "success",
      });
      onAccept();
    } catch (error) {
      console.error("Error accepting consent:", error);
      enqueueSnackbar("Fehler beim Bestätigen der Einwilligung", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = () => {
    onDecline();
  };

  // Opens the PDF in its own tab rather than through the MDD form engine's
  // DocumentViewer dialog, which stays in apps/schadenportal (entangled with
  // claim-domain code) and is not available here. The tab is opened before the
  // download so it still counts as a direct result of the click — opened after
  // the await, pop-up blockers would suppress it silently.
  const handleOpenDocument = async (e) => {
    e.preventDefault();
    const document_window = window.open("", "_blank");
    if (!document_window) {
      enqueueSnackbar(
        "Das Dokument konnte nicht geöffnet werden. Bitte Pop-ups für diese Seite erlauben.",
        { variant: "error" }
      );
      return;
    }
    document_window.opener = null;
    try {
      const blob = await downloadConsentDocument(versionInfo.id);
      const url = window.URL.createObjectURL(blob);
      document_window.location.href = url;
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      document_window.close();
      enqueueSnackbar("Fehler beim Laden des Dokuments", { variant: "error" });
    }
  };

  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (!versionInfo) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {}}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle>
          <Typography variant="h2" component="div">
            Zustimmung erforderlich
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {versionInfo.description && (
              <Typography variant="body1" color="text.secondary">
                {versionInfo.description}
              </Typography>
            )}

            <Box
              sx={{
                p: 2,
                bgcolor: "background.paper",
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="h4" color="text.secondary" gutterBottom>
                <strong>{versionInfo.title}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Version: <strong>{versionInfo.version}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gültig ab:{" "}
                <strong>{formatDate(versionInfo.effective_from)}</strong>
              </Typography>
            </Box>

            <Typography variant="body1">
              Bitte lesen Sie{" "}
              <Link
                component="button"
                onClick={handleOpenDocument}
                underline="always"
                sx={{ verticalAlign: "baseline" }}
              >
                {versionInfo.title} (PDF)
              </Link>{" "}
              sorgfältig durch, bevor Sie fortfahren.
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  Ich habe das Dokument gelesen und stimme ihm zu.
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "16px", gap: 1, alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Ablehnen beendet Ihre Sitzung.
          </Typography>
          <Button variant="tertiary" onClick={handleDecline} disabled={isLoading}>
            Ablehnen
          </Button>
          <Button
            variant="primary"
            onClick={handleAccept}
            disabled={!isChecked || isLoading}
          >
            {isLoading ? "Wird gespeichert..." : "Zustimmen und fortfahren"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

ConsentDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  consentStatus: PropTypes.object,
  documentType: PropTypes.string,
  onAccept: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
};

export default ConsentDocumentModal;
